/* ------------------------------------------------------------------
   DashboardLayout.js   (responsive v3.0 – Buffer-style modernized)
   • Dark sidebar with clean nav links + hover states
   • Polished header bar with compact usage pills
   • Consistent with AccountSelector theme (blue accents, card style)
------------------------------------------------------------------ */

import React, { useContext, useEffect, useState } from 'react'
import { Spinner, Offcanvas, Navbar, Button, Collapse } from 'react-bootstrap'
import { NavLink, Link, Outlet, useLocation } from 'react-router-dom'
import axios from 'axios'
import { UserContext } from '../../context/UserContext'
import WelcomeBar from './WelcomeBar'
import {
  Images, Calendar2Event, People, Folder, Gear, List,
  Lightning, CreditCard2Front, Link45deg,
  Megaphone, PersonVcard, Envelope, ChatDots, ChevronDown, ChevronRight,
  ShieldLock, Stars, Magic, Broadcast, RocketTakeoff, BarChartLine,
} from 'react-bootstrap-icons'
import { ANALYTICS_LAUNCH_DATE, isNewFeature } from '../../constants'

const API_BASE = '/api'

/* ─────────────────────────── Sidebar Nav Link ─────────────────────────── */
const SideLink = ({ to, label, Icon, onClick, indent = false, badge = null }) => (
  <NavLink
    to={to}
    end
    onClick={onClick}
    className='text-decoration-none'
    style={({ isActive }) => ({
      display       : 'flex',
      alignItems    : 'center',
      gap           : 10,
      padding       : indent ? '8px 14px 8px 34px' : '9px 14px',
      borderRadius  : 8,
      marginBottom  : 2,
      fontWeight    : 500,
      fontSize      : indent ? 13.5 : 14,
      letterSpacing : '0.01em',
      color         : isActive ? '#ffffff' : 'rgba(255,255,255,0.55)',
      background    : isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
      transition    : 'all 0.15s ease',
      cursor        : 'pointer',
    })}
    onMouseEnter={e => {
      const el = e.currentTarget
      if (!el.classList.contains('active')) {
        el.style.color      = 'rgba(255,255,255,0.85)'
        el.style.background = 'rgba(255,255,255,0.07)'
      }
    }}
    onMouseLeave={e => {
      const el = e.currentTarget
      if (!el.classList.contains('active')) {
        el.style.color      = 'rgba(255,255,255,0.55)'
        el.style.background = 'transparent'
      }
    }}
  >
    <Icon size={indent ? 16 : 17} style={{ flexShrink: 0 }} />
    {label}
    {badge && (
      <span style={{
        fontSize: 9, fontWeight: 700, letterSpacing: '0.5px', background: '#2563eb', color: '#fff',
        padding: '2px 6px', borderRadius: 6,
      }}>{badge}</span>
    )}
  </NavLink>
)

/* ── "Coming soon" sub-item (disabled) ── */
const SoonLink = ({ label, Icon }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px 8px 34px',
    fontSize: 13.5, color: 'rgba(255,255,255,0.3)', cursor: 'default', marginBottom: 2,
  }}>
    <Icon size={16} style={{ flexShrink: 0 }} />
    <span style={{ flex: 1 }}>{label}</span>
    <span style={{
      fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
      background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', padding: '2px 6px', borderRadius: 6,
    }}>Soon</span>
  </div>
)

/* ── Collapsible sidebar group (Marketing, AI Studio, Social Media …) ──
   Auto-expands when the current route matches any of `prefixes`. */
const NavGroup = ({ label, Icon, prefixes = [], children }) => {
  const location = useLocation()
  const active = prefixes.some(p => location.pathname.startsWith(p))
  const [open, setOpen] = useState(active)
  useEffect(() => { if (active) setOpen(true) }, [active])

  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
          padding: '9px 14px', borderRadius: 8, marginBottom: 2,
          fontWeight: 500, fontSize: 14, letterSpacing: '0.01em', textAlign: 'left',
          color: active ? '#ffffff' : 'rgba(255,255,255,0.55)',
          background: 'transparent', border: 'none', cursor: 'pointer',
        }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.color = 'rgba(255,255,255,0.85)' }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.color = 'rgba(255,255,255,0.55)' }}
      >
        <Icon size={17} style={{ flexShrink: 0 }} />
        <span style={{ flex: 1 }}>{label}</span>
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
      </button>
      <Collapse in={open}>
        <div>{children}</div>
      </Collapse>
    </>
  )
}

/* ─────────────────────────── Usage Pill ─────────────────────────────── */
const UsagePill = ({ label, used, limit, color }) => {
  if (!limit) return null
  const pct  = Math.min(100, Math.round((used / limit) * 100))
  const left = Math.max(limit - used, 0)
  const barColor =
    pct > 75 ? '#ef4444' :
    pct > 50 ? '#f59e0b' :
    color || '#2563eb'

  return (
    <div style={{
      display       : 'flex',
      flexDirection : 'column',
      gap           : 4,
      padding       : '8px 14px',
      background    : '#f8fafc',
      border        : '1px solid #e5e7eb',
      borderRadius  : 10,
      minWidth      : 130,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {label}
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, color: pct > 75 ? '#ef4444' : '#111827' }}>
          {left.toLocaleString()} left
        </span>
      </div>
      {/* Track */}
      <div style={{ height: 5, borderRadius: 99, background: '#e5e7eb', overflow: 'hidden' }}>
        <div style={{
          height     : '100%',
          width      : `${pct}%`,
          borderRadius: 99,
          background  : barColor,
          transition  : 'width 0.4s ease',
        }} />
      </div>
    </div>
  )
}

/* ─────────────────────────── Header Bar ─────────────────────────────── */
const HeaderBar = ({ usage, loading }) => {
  if (loading)
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 0', marginBottom: 20 }}>
        <Spinner animation='border' size='sm' style={{ color: '#d1d5db' }} />
      </div>
    )

  const postLimit  = usage?.posts?.limit    ?? 0
  const postUsed   = usage?.posts?.used     ?? 0
  const acctLimit  = usage?.accounts?.limit ?? 0
  const acctUsed   = usage?.accounts?.used  ?? 0
  const aiLimit    = usage?.ai?.limit       ?? 0
  const aiUsed     = usage?.ai?.used        ?? 0
  const aiLeft     = usage?.ai?.left        ?? Math.max(aiLimit - aiUsed, 0)
  const aiImgLimit = usage?.aiImages?.limit ?? 0
  const aiImgUsed  = usage?.aiImages?.used  ?? 0

  return (
    <div style={{
      background    : '#ffffff',
      border        : '1px solid #e5e7eb',
      borderRadius  : 12,
      padding       : '12px 18px',
      marginBottom  : 24,
      display       : 'flex',
      flexWrap      : 'wrap',
      alignItems    : 'center',
      justifyContent: 'flex-end',
      gap           : 10,
      boxShadow     : '0 1px 3px rgba(0,0,0,0.05)',
    }}>

      {/* Usage pills */}
      {postLimit > 0 && (
        <UsagePill label='Posts' used={postUsed} limit={postLimit} color='#2563eb' />
      )}
      {postLimit === 0 && (
        <div style={{
          padding    : '6px 12px',
          background : '#fef2f2',
          border     : '1px solid #fecaca',
          borderRadius: 8,
          fontSize   : 12,
          fontWeight : 600,
          color      : '#dc2626',
        }}>
          Posting disabled
        </div>
      )}

      <UsagePill label='Accounts' used={acctUsed} limit={acctLimit} color='#0891b2' />

      {aiLimit > 0 && (
        <UsagePill label='AI Credits' used={aiUsed} limit={aiLimit} color='#7c3aed' />
      )}

      {aiImgLimit > 0 && (
        <UsagePill label='Bulk Posts' used={aiImgUsed} limit={aiImgLimit} color='#4f46e5' />
      )}

      {/* Divider */}
      <div style={{ width: 1, height: 36, background: '#e5e7eb', margin: '0 4px' }} />

      {/* Action buttons */}
      <Link to='/dashboard/plans' style={{ textDecoration: 'none' }}>
        <button style={{
          display     : 'flex',
          alignItems  : 'center',
          gap         : 6,
          padding     : '8px 16px',
          background  : '#2563eb',
          color       : '#fff',
          border      : 'none',
          borderRadius: 8,
          fontSize    : 13,
          fontWeight  : 600,
          cursor      : 'pointer',
          transition  : 'background 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#1d4ed8'}
        onMouseLeave={e => e.currentTarget.style.background = '#2563eb'}
        >
          <Lightning size={13} />
          Upgrade
        </button>
      </Link>

      <Link to='/dashboard/credits' style={{ textDecoration: 'none' }}>
        <button style={{
          display     : 'flex',
          alignItems  : 'center',
          gap         : 6,
          padding     : '8px 14px',
          background  : 'transparent',
          color       : '#374151',
          border      : '1px solid #d1d5db',
          borderRadius: 8,
          fontSize    : 13,
          fontWeight  : 600,
          cursor      : 'pointer',
          transition  : 'all 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.color = '#2563eb' }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#374151' }}
        >
          <CreditCard2Front size={13} />
          Buy Credits
        </button>
      </Link>
    </div>
  )
}

/* ─────────────────────────── Sidebar Body ────────────────────────────── */
const SidebarBody = ({ hideLogo = false, onNavigate }) => {
  const [isAdmin, setIsAdmin] = useState(false)
  useEffect(() => {
    let alive = true
    axios.get(`${API_BASE}/admin/me`, { withCredentials: true })
      .then(({ data }) => { if (alive) setIsAdmin(!!data.isAdmin) })
      .catch(() => {})
    return () => { alive = false }
  }, [])
  return (
  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
    {!hideLogo && (
      <div style={{ padding: '24px 16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 12 }}>
        <img
          src='/assets/logo/greatly-social-full.png'
          alt='Greatly Social'
          style={{ width: 148, filter: 'brightness(0) invert(1)' }}
        />
      </div>
    )}

    <nav style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px 10px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', padding: '6px 14px 8px' }}>
        Quickstart
      </div>
      <SideLink to='/dashboard/quickstart' label='Quickstart' Icon={RocketTakeoff} onClick={onNavigate} />

      <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '10px 4px' }} />

      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', padding: '6px 14px 8px' }}>
        AI Studio
      </div>
      <NavGroup label='AI Studio' Icon={Stars} prefixes={['/dashboard/studio']}>
        <SideLink to='/dashboard/studio/bulk' label='AI Bulk Post Creation' Icon={Magic} onClick={onNavigate} indent />
      </NavGroup>

      <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '10px 4px' }} />

      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', padding: '6px 14px 8px' }}>
        Social Media
      </div>
      <NavGroup label='Social Media' Icon={Broadcast} prefixes={['/dashboard/post', '/dashboard/calendar', '/dashboard/analytics', '/dashboard/accounts', '/dashboard/link-in-bio']}>
        <SideLink to='/dashboard/post'        label='Post'        Icon={Images}         onClick={onNavigate} indent />
        <SideLink to='/dashboard/calendar'    label='Calendar'    Icon={Calendar2Event} onClick={onNavigate} indent />
        <SideLink to='/dashboard/analytics'   label='Analytics'   Icon={BarChartLine}   onClick={onNavigate} indent
                  badge={isNewFeature(ANALYTICS_LAUNCH_DATE) ? 'NEW' : null} />
        <SideLink to='/dashboard/accounts'    label='Accounts'    Icon={People}         onClick={onNavigate} indent />
        <SideLink to='/dashboard/link-in-bio' label='Link in Bio' Icon={Link45deg}      onClick={onNavigate} indent />
      </NavGroup>

      <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '10px 4px' }} />

      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', padding: '6px 14px 8px' }}>
        Marketing
      </div>
      <NavGroup label='Marketing' Icon={Megaphone} prefixes={['/dashboard/marketing']}>
        <SideLink to='/dashboard/marketing/crm'   label='CRM'             Icon={PersonVcard} onClick={onNavigate} indent />
        <SideLink to='/dashboard/marketing/email' label='Email Marketing' Icon={Envelope}    onClick={onNavigate} indent />
        <SoonLink label='SMS Marketing' Icon={ChatDots} />
      </NavGroup>

      <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '10px 4px' }} />

      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', padding: '6px 14px 8px' }}>
        Account
      </div>
      <SideLink to='/dashboard/files'    label='Files'    Icon={Folder} onClick={onNavigate} />
      <SideLink to='/dashboard/settings' label='Settings' Icon={Gear}   onClick={onNavigate} />
      {isAdmin && <SideLink to='/dashboard/admin' label='Admin' Icon={ShieldLock} onClick={onNavigate} />}
    </nav>

    {/* Bottom brand footer */}
    <div style={{ padding: '14px 16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', textAlign: 'center' }}>
        Greatly Social
      </div>
    </div>
  </div>
  )
}

/* ─────────────────────────── Layout ──────────────────────────────────── */
export default function DashboardLayout () {
  const { user }              = useContext(UserContext)
  const [usage, setUsage]     = useState(null)
  const [loading, setLoading] = useState(true)
  const [showMenu, setShowMenu] = useState(false)

  const SIDEBAR_BG = '#0f172a'   // deep navy — Buffer-style dark sidebar

  useEffect(() => {
    ;(async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/billing/usage`, { withCredentials: true })
        setUsage(data)
      } catch (err) {
        console.error('[Usage] fetch failed:', err)
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>

      {/* ── MOBILE TOP BAR ── */}
      <Navbar
        className='d-md-none border-bottom'
        style={{ background: SIDEBAR_BG, padding: '8px 16px' }}
      >
        <Button
          variant='link'
          style={{ color: 'rgba(255,255,255,0.7)', padding: 4 }}
          onClick={() => setShowMenu(true)}
        >
          <List size={24} />
        </Button>
        <Navbar.Brand className='ms-2'>
          <img
            src='/assets/logo/greatly-social-full.png'
            alt='Greatly Social'
            style={{ width: 120, filter: 'brightness(0) invert(1)' }}
          />
        </Navbar.Brand>
      </Navbar>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>

        {/* ── DESKTOP SIDEBAR ── */}
        <div
          className='d-none d-md-flex flex-column'
          style={{
            width    : 220,
            minWidth : 220,
            position : 'fixed',
            top      : 0,
            bottom   : 0,
            left     : 0,
            zIndex   : 1020,
            background: SIDEBAR_BG,
          }}
        >
          <SidebarBody />
        </div>

        {/* ── MOBILE OFFCANVAS ── */}
        <Offcanvas
          show={showMenu}
          onHide={() => setShowMenu(false)}
          className='d-md-none'
          scroll
          style={{ background: SIDEBAR_BG, width: 240 }}
        >
          <Offcanvas.Header closeButton closeVariant='white' style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <Offcanvas.Title>
              <img
                src='/assets/logo/greatly-social-full.png'
                alt='Greatly Social'
                style={{ width: 140, filter: 'brightness(0) invert(1)' }}
              />
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className='pt-2' style={{ padding: '8px 0' }}>
            <SidebarBody hideLogo onNavigate={() => setShowMenu(false)} />
          </Offcanvas.Body>
        </Offcanvas>

        {/* ── MAIN CONTENT ── */}
        <div
          className='dash-main'
          style={{
            flex      : 1,
            minWidth  : 0,
            marginLeft: 220,   // offset for fixed sidebar on desktop
            padding   : '28px 28px 40px',
          }}
        >
          {/* Remove the marginLeft on mobile */}
          <style>{`
            @media (max-width: 767px) {
              .dash-main { margin-left: 0 !important; padding: 16px !important; }
            }
          `}</style>

          <HeaderBar usage={usage} loading={loading} />
          <WelcomeBar />
          <Outlet />
        </div>
      </div>
    </div>
  )
}